// The students list's bulk actions and toolbar, shared by StudentToolbar.jsx,
// useStudentList.js, DeleteStudentsDialog.jsx, StudentsPage.jsx and the phones,
// so a phone's multi-select says and writes what the browser's does.
//
// The delete itself — which students a delete removes and which it retires,
// the probe, the confirm and the notice when a probe could not answer — is
// `planStudentDeletion` and `uncertainDeletionMessage` in ./people.js. What is
// here is the rest of the menu and its bar.

// ── The rules are `core/src/roster.rs`'s ───────────────────────────────────
import { coreSync } from './core.js';

// "Actions", or "Actions (3)" with a selection.
export const actionsLabel = (selectedCount) => coreSync().actionsLabel(Number(selectedCount) || 0);

// A status item on the menu. The stored word: "Mark as waiting", not "Mark as
// Waiting List".
export const markAsLabel = (status) => coreSync().markAsLabel(String(status));

// ── The Status filter, a multi-select since #574 ───────────────────────────
//
// `roster_count_label` in `core/src/roster.rs` takes one status at a time,
// and so does the phones' filter. The functions below are web-only because
// they are the rule for a shape (a *set* of statuses) the core does not take,
// not a twin of a Rust rule. They delegate to the core for the two shapes it
// knows — one status, or every one of them.
//
// **Selecting every status is "no filter", not "these five statuses"**: a
// status outside STATUSES is filtered out of every single-status list (see
// `optionalEnum` in firestore.rules), so ticking every box matches
// unconditionally and a student with a malformed status (#177) stays on
// screen (#574).
export const isAllStatuses = (selected, allStatuses) => selected.size === allStatuses.length;

// Whether a student's (already-normalised) status is on screen.
export function statusFilterMatches(status, selected, allStatuses) {
  if (isAllStatuses(selected, allStatuses)) return true;
  return selected.has(status);
}

// The closed control's text: "All statuses", one or two labels spelled out,
// or a count once there isn't room to. Ours: MMS's own filter takes one
// status at a time (#177).
export function statusFilterSummary(selected, allStatuses, labelOf) {
  if (isAllStatuses(selected, allStatuses)) return 'All statuses';
  const chosen = allStatuses.filter((s) => selected.has(s));
  if (chosen.length === 0) return 'No statuses';
  if (chosen.length <= 2) return chosen.map(labelOf).join(', ');
  return `${chosen.length} statuses`;
}

// "12 active", "7 active, trial", "40 total". The one- and every-status
// shapes are the core's own words; the shape in between — more than one,
// fewer than all — is ours to join, in STATUSES order rather than click
// order.
export function rosterCountLabel(count, selected, allStatuses) {
  const n = Number(count) || 0;
  if (isAllStatuses(selected, allStatuses)) return coreSync().rosterCountLabel(n, 'all');
  const chosen = allStatuses.filter((s) => selected.has(s));
  if (chosen.length === 1) return coreSync().rosterCountLabel(n, chosen[0]);
  if (chosen.length === 0) return `${n} selected`;
  return `${n} ${chosen.join(', ')}`;
}

// What marking the selection writes to each student — a merge, one student at
// a time, so one refused does not undo the others. `updatedAt` is the
// provider's; the core's document closes with it.
export const bulkStatusPatch = (status) => {
  const [first] = coreSync().bulkStatusDocument(String(status));
  return { [first.field]: first.text };
};

// The selection kept to the rows still on screen: a student filtered or
// searched away is no longer selected. The same set back when nothing left.
export function pruneSelection(selected, visibleIds) {
  if (selected.size === 0) return selected;
  const kept = coreSync().pruneSelection([...selected].map(String), [...visibleIds].map(String));
  return kept.length === selected.size ? selected : new Set(kept);
}

// Whether the header's box is ticked: something on screen, all of it selected.
export const allRowsSelected = (rowCount, selectedCount) => coreSync().allRowsSelected(Number(rowCount) || 0, Number(selectedCount) || 0);

// The line under the confirm while a delete checks each student's history.
export const deleteProgressLine = ({ checked, total }) => coreSync().deleteProgressLine(Number(checked) || 0, Number(total) || 0);

// The toolbar's words.
export const ROSTER_WORDING = Object.freeze({
  addNew: '+ Add New',
  deleteStudents: 'Delete Students',
  allStatuses: 'All statuses',
  filterLabel: 'Filter by status',
  columns: 'Columns ▾',
  sortAscending: 'Sort ↑',
  sortDescending: 'Sort ↓',
  search: 'Search…',
  searchLabel: 'Search students',
  selectAll: 'Select all students',
});
